/**
 * Hook d'authentification SIMPLIFIÉ - Version sans bugs
 * Approche minimaliste pour éliminer les problèmes de redirection
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_HOST } from '../config/api.config.js';
import logger from '@/utils/logger';
import { getToken, setToken, clearToken } from '@/utils/storage';

const AuthContext = createContext();

/**
 * Décode une chaîne base64 en UTF-8 pour préserver les caractères spéciaux (é, à, è, etc.).
 * atob() seul interprète les bytes en Latin-1 ; on utilise TextDecoder pour UTF-8.
 */
function base64ToUtf8(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new TextDecoder('utf-8').decode(bytes);
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const API_BASE_URL = API_HOST;

  // Initialisation simple au montage du composant
  useEffect(() => {
    // logger.debug("🚀 Initialisation auth simplifiée...");

    const token = getToken();

    if (token) {
      let payload = null;

      try {
        const parts = token.trim().split('.');
        // JWT = 3 parties (header.payload.signature)
        if (parts.length === 3) {
          payload = JSON.parse(base64ToUtf8(parts[1]));
          // logger.debug("🔓 Token JWT décodé:", { user_id: payload.user_id, email: payload.email, exp: payload.exp });
        } else {
          // Token base64 (format backend : JSON encodé en base64 UTF-8)
          try {
            payload = JSON.parse(base64ToUtf8(token.trim()));
            // logger.debug("🔓 Token base64 décodé:", { user_id: payload.user_id, email: payload.email, exp: payload.exp });
          } catch {
            throw new Error('Format token inconnu (ni JWT ni base64)');
          }
        }

        const currentTime = Math.floor(Date.now() / 1000);
        const exp = payload.exp;
        if (exp && exp < currentTime) {
          logger.debug('⏰ Token expiré, nettoyage');
          clearToken();
        } else {
          setUser({
            id: payload.user_id ?? payload.userId ?? payload.sub,
            email: payload.email,
            role: payload.role,
            school_id: payload.school_id ?? payload.schoolId ?? null,
            first_name: payload.first_name ?? '',
            last_name: payload.last_name ?? '',
            school: {
              id: payload.school_id ?? payload.schoolId ?? null,
              name: payload.school_name ?? '',
              code: payload.school_code ?? '',
              subscription_plan: payload.subscription_plan ?? 'basic',
            },
            can_switch_schools: payload.can_switch_schools ?? false,
            token: token.trim(),
          });
          // logger.debug("✅ Utilisateur connecté:", payload.email);
        }
      } catch (error) {
        logger.debug('⚠️ Token invalide ou indécodable:', error.message);
        clearToken();
        setUser(null);
      }
    } else {
      logger.debug('⚠️ Aucun token trouvé');
    }

    setLoading(false);
  }, []);

  const login = async (credentials, rememberMe = false) => {
    try {
      logger.debug('🔐 [LOGIN] Tentative de connexion...');
      logger.debug('🔐 [LOGIN] API_BASE_URL:', API_BASE_URL);
      logger.debug('🔐 [LOGIN] URL complète:', `${API_BASE_URL}/api/auth/login`);
      logger.debug('🔐 [LOGIN] Credentials:', {
        email: credentials.email,
        password: '***',
      });

      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      logger.debug('📡 [LOGIN] Response status:', response.status, response.statusText);
      logger.debug('📡 [LOGIN] Response ok:', response.ok);

      const data = await response.json();
      logger.debug('📦 [LOGIN] Response data:', {
        success: data.success,
        hasData: !!data.data,
        hasToken: !!data.data?.token,
        tokenStart: data.data?.token ? data.data.token.substring(0, 30) + '...' : 'NO TOKEN',
        tokenLength: data.data?.token?.length || 0,
        hasUser: !!data.data?.user,
        userEmail: data.data?.user?.email,
        require_password_change: data.require_password_change,
      });

      if (response.ok && data.success) {
        const { user: userData, token } = data.data;

        // Stocker le token - TOUJOURS dans localStorage pour persistance
        logger.debug('💾 [LOGIN] Stockage du token...', {
          rememberMe,
          tokenLength: token?.length || 0,
          tokenStart: token ? token.substring(0, 30) + '...' : 'TOKEN MANQUANT',
          tokenType: token?.startsWith('eyJ') ? 'JWT' : 'base64',
          storageType: 'localStorage (forcé pour persistance)',
        });

        // Vérifier si le token existe et le normaliser en string
        const tokenStr = token == null ? '' : typeof token === 'string' ? token.trim() : String(token);
        if (!tokenStr) {
          logger.error('❌ [LOGIN] Token manquant ou vide dans la réponse API');
          throw new Error("Token d'authentification manquant");
        }

        // Vérifier le type de token (JWT ou base64)
        if (tokenStr.startsWith('eyJ')) {
          logger.debug('✅ [LOGIN] Token JWT reçu du backend');
        } else {
          logger.debug('✅ [LOGIN] Token base64 reçu du backend');
        }

        // rememberMe = true  → localStorage  (persiste après fermeture du navigateur)
        // rememberMe = false → sessionStorage (expire à la fermeture de l'onglet)
        try {
          setToken(tokenStr, rememberMe);
          logger.debug('💾 [LOGIN] Token stocké', {
            storage: rememberMe ? 'localStorage' : 'sessionStorage',
          });
        } catch (storageErr) {
          logger.error('❌ [LOGIN] Erreur stockage token:', storageErr);
          throw new Error('Impossible de stocker le token (storage indisponible)');
        }

        // Définir l'utilisateur connecté IMMÉDIATEMENT
        setUser({
          ...userData,
          token: tokenStr,
        });

        logger.debug('✅ Connexion réussie et état forcé:', userData.email);

        // Retourner aussi require_password_change pour que le composant Login puisse rediriger
        return {
          success: true,
          data: userData,
          require_password_change: data.require_password_change,
        };
      } else {
        return {
          success: false,
          error: data.error || 'Échec de la connexion',
        };
      }
    } catch (error) {
      logger.error('Erreur de connexion:', error);
      return {
        success: false,
        error: error.message || 'Erreur de connexion au serveur',
      };
    }
  };

  const logout = useCallback(() => {
    logger.debug('🚪 Déconnexion...');

    // Nettoyer le stockage
    clearToken();

    // Réinitialiser l'état utilisateur
    setUser(null);

    logger.debug('✅ Déconnexion terminée');
  }, []);

  const value = {
    user,
    isAuthenticated: !!user && !!user.token,
    loading,
    initialized: true, // Toujours initialisé dans cette version simple
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
